import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { FilePdf, Receipt, CheckCircle, WarningCircle, Bank, Files } from "@phosphor-icons/react";
import api from "../lib/api";

const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtD = (v) => { const m = String(v || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}-${_MON[+m[2] - 1]}-${m[1].slice(2)}` : (v || "—"); };
const num = (v) => (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const th = { padding: "9px 10px", fontSize: 11, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };
const lbl = { fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 };

const StatCard = ({ label, value, sub, color, testid }) => (
  <div data-testid={testid} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 18px", minWidth: 140 }}>
    <div style={lbl}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || "#0F172A", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{sub}</div>}
  </div>
);

export default function LegrandReceiptsPanel() {
  const [legrand, setLegrand] = useState(null);
  const [hdfc, setHdfc] = useState([]);
  const [job, setJob] = useState(null);     // {status, stage, progress:{done,total}}
  const [data, setData] = useState(null);
  const pollRef = useRef(null);
  const busy = !!job;

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = async (jobId) => {
    try {
      const { data: s } = await api.get(`/fa-logistics/legrand-advice/status/${jobId}`);
      setJob({ status: s.status, stage: s.stage, progress: s.progress || { done: 0, total: 0 } });
      if (s.status === "done") {
        setData(s.result); setJob(null);
        const t = s.result?.totals || {};
        toast.success(`Matched ${t.matched} of ${t.lines} line(s) to a bank advice`);
        return;
      }
      if (s.status === "error") {
        toast.error(s.error || "Legrand match failed"); setJob(null);
        return;
      }
      pollRef.current = setTimeout(() => poll(jobId), 2000);
    } catch {
      toast.error("Lost connection to the match job"); setJob(null);
    }
  };

  const parse = async () => {
    if (!legrand || !hdfc.length) { toast.error("Choose the Legrand advice (PDF) and at least one HDFC advice (PDF)"); return; }
    setData(null);
    setJob({ status: "queued", stage: "Starting…", progress: { done: 0, total: hdfc.length } });
    try {
      const fd = new FormData();
      fd.append("legrand_advice", legrand);
      hdfc.forEach((f) => fd.append("hdfc_advices", f));
      const { data: start } = await api.post("/fa-logistics/legrand-advice/parse", fd);
      poll(start.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the Legrand match");
      setJob(null);
    }
  };

  const rows = data?.preview || [];
  const t = data?.totals || {};
  const pct = job?.progress?.total ? Math.round((job.progress.done / job.progress.total) * 100) : 0;

  const [sel, setSel] = useState({});
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState(null);

  useEffect(() => {
    // default-select every matched row when a fresh result arrives
    const init = {};
    (data?.preview || []).forEach((r, i) => { if (r.matched) init[i] = true; });
    setSel(init); setPostResult(null);
  }, [data]);

  const matchedIdx = rows.map((r, i) => ({ r, i })).filter((x) => x.r.matched);
  const selList = matchedIdx.filter((x) => sel[x.i]);
  const toggleRow = (i) => setSel((s) => ({ ...s, [i]: !s[i] }));
  const allSel = matchedIdx.length > 0 && matchedIdx.every((x) => sel[x.i]);
  const toggleAll = () => { const v = !allSel; const n = {}; matchedIdx.forEach((x) => { n[x.i] = v; }); setSel(n); };

  const post = async (dryRun) => {
    if (!selList.length) { toast.error("Select at least one matched invoice"); return; }
    if (!dryRun && !window.confirm(
      `Post ${selList.length} receipt voucher(s) to the ERP?\n\nUses the same posting as Record Receipts and is idempotent (re-posting the same advice is safe).`)) return;
    setPosting(true); setPostResult(null);
    try {
      const { data: res } = await api.post("/fa-logistics/legrand-advice/post", {
        rows: selList.map((x) => x.r), dry_run: !!dryRun,
      });
      setPostResult({ ...res, dry_run: !!dryRun });
      if (dryRun) toast.success(`Dry-run OK — ${res.would_post ?? 0} voucher(s) would post`);
      else toast.success(`Posted ${res.posted} · ${res.already_recorded} already recorded · ${res.failed} failed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Posting failed");
    } finally { setPosting(false); }
  };
  const resBySale = {};
  (postResult?.results || []).forEach((x) => { resBySale[x.sale_no] = x; });

  return (
    <div data-testid="legrand-panel">
      <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 14px" }}>
        Upload the <b>Legrand buyer advice (PDF)</b> and the <b>HDFC bank advices (PDF, one per invoice)</b>. Each Legrand
        line's export invoice is looked up in the ERP to get its <b>GST invoice number</b>, which is matched to the HDFC advice
        carrying it — pulling the <b>bank reference (127…)</b> and FX rate. Review the matches, then <b>post the selected</b> receipts
        (full USD + any bank charge) — the same idempotent path as <b>Record Receipts</b>.
      </p>

      {/* Upload controls */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div>
          <div style={lbl}>Legrand Buyer Advice (.pdf)</div>
          <label data-testid="legrand-advice-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "1px dashed #94A3B8", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#334155", background: "#F8FAFC", maxWidth: 280 }}>
            <FilePdf size={18} weight="fill" color="#DC2626" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{legrand ? legrand.name : "Choose Legrand advice…"}</span>
            <input data-testid="legrand-advice-input" type="file" accept=".pdf" onChange={(e) => { setLegrand(e.target.files?.[0] || null); setData(null); }} style={{ display: "none" }} />
          </label>
        </div>
        <div>
          <div style={lbl}>HDFC Bank Advices (.pdf, multiple)</div>
          <label data-testid="legrand-hdfc-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", border: "1px dashed #94A3B8", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#334155", background: "#F8FAFC", maxWidth: 280 }}>
            <Files size={18} weight="fill" color="#0369A1" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hdfc.length ? `${hdfc.length} file(s) chosen` : "Choose HDFC advices…"}</span>
            <input data-testid="legrand-hdfc-input" type="file" accept=".pdf" multiple onChange={(e) => { setHdfc(Array.from(e.target.files || [])); setData(null); }} style={{ display: "none" }} />
          </label>
        </div>
        <div style={{ flex: 1 }} />
        <button data-testid="legrand-parse-btn" onClick={parse} disabled={busy || !legrand || !hdfc.length}
          className="jde-btn jde-btn-primary" style={{ opacity: (!legrand || !hdfc.length) ? 0.5 : 1 }}>
          {busy ? "Reading & matching…" : "Parse & match"}
        </button>
      </div>

      {job && (
        <div data-testid="legrand-progress" style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#334155", marginBottom: 8 }}>
            <span style={{ fontWeight: 700 }}>{job.stage || "Working…"}</span>
            <span style={{ fontVariantNumeric: "tabular-nums", color: "#64748B" }}>{job.progress?.done || 0} / {job.progress?.total || 0} bank advices</span>
          </div>
          <div style={{ height: 10, background: "#F1F5F9", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#0EA5E9,#0369A1)", borderRadius: 999, transition: "width .4s ease" }} />
          </div>
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 8 }}>Running in the background — you can keep this tab open; results appear automatically.</div>
        </div>
      )}

      {data && !busy && (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <div data-testid="legrand-header-card" style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 12, padding: "14px 18px", minWidth: 280, flex: "1 1 280px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                <Bank size={18} weight="fill" color="#0369A1" />
                <span style={{ fontSize: 13, fontWeight: 800, color: "#0C4A6E" }}>Legrand Remittance Advice</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 12.5, color: "#0C4A6E" }}>
                <div>Payment ref: <b>{data.header?.payment_reference || "—"}</b></div>
                <div>Currency: <b>{data.header?.currency || "—"}</b></div>
                <div style={{ gridColumn: "1 / span 2" }}>Advice total: <b>{data.header?.currency} {num(data.header?.total_amount)}</b></div>
              </div>
            </div>
            <StatCard testid="legrand-lines" label="Legrand Lines" value={t.lines || 0} color="#0F172A" />
            <StatCard testid="legrand-matched-count" label="Matched to HDFC" value={t.matched || 0} sub="bank ref found" color="#166534" />
            <StatCard testid="legrand-unmatched-count" label="Unmatched" value={t.unmatched || 0} sub="no HDFC advice" color="#B45309" />
            <StatCard testid="legrand-total-usd" label="Total (USD)" value={num(t.total_usd)} color="#0F172A" />
          </div>

          {(data.advice_errors || []).length > 0 && (
            <div data-testid="legrand-advice-errors" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 12.5, color: "#991B1B" }}>
              <b>{data.advice_errors.length} HDFC advice(s) could not be read:</b> {data.advice_errors.map((e) => e.filename).join(", ")}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <button data-testid="legrand-dryrun-btn" onClick={() => post(true)} disabled={posting || !selList.length}
              className="jde-btn" style={{ border: "1px solid #CBD5E1", background: "#fff", fontWeight: 700, opacity: !selList.length ? 0.5 : 1 }}>
              Dry run
            </button>
            <button data-testid="legrand-post-btn" onClick={() => post(false)} disabled={posting || !selList.length}
              className="jde-btn jde-btn-primary" style={{ opacity: !selList.length ? 0.5 : 1 }}>
              {posting ? "Posting…" : `Post selected (${selList.length})`}
            </button>
            <span style={{ fontSize: 12.5, color: "#64748B" }}>
              Posts one receipt voucher per selected invoice (full USD + bank charge when present) — same as Record Receipts, idempotent.
            </span>
          </div>

          {postResult && (
            <div data-testid="legrand-post-result" style={{ background: postResult.failed ? "#FEF2F2" : "#ECFDF5", border: `1px solid ${postResult.failed ? "#FECACA" : "#A7F3D0"}`, borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13, color: postResult.failed ? "#991B1B" : "#065F46" }}>
              {postResult.dry_run ? <b>Dry run — nothing was written. {postResult.would_post ?? 0} voucher(s) would post. </b> : <><b>Posted {postResult.posted}</b> · already recorded {postResult.already_recorded} · failed {postResult.failed}</>}
            </div>
          )}

          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto", marginBottom: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="legrand-preview-table">
              <thead>
                <tr style={{ background: "#0F172A" }}>
                  <th style={{ ...th, width: 34 }}><input data-testid="legrand-select-all" type="checkbox" checked={allSel} onChange={toggleAll} /></th>
                  <th style={th}>Legrand Ref</th>
                  <th style={th}>Export Inv</th>
                  <th style={th}>GST Invoice #</th>
                  <th style={th}>Party</th>
                  <th style={th}>ERP Sale No</th>
                  <th style={{ ...th, textAlign: "right" }}>Amount (USD)</th>
                  <th style={{ ...th, textAlign: "right" }}>Rate</th>
                  <th style={{ ...th, textAlign: "right" }}>Bank Charge</th>
                  <th style={th}>Bank Ref (127…)</th>
                  <th style={th}>Value Date</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const pr = resBySale[r.sale_no];
                  return (
                  <tr key={i} data-testid={`legrand-row-${i}`} style={{ background: r.matched ? "transparent" : "#FFF7ED" }}>
                    <td style={td}><input data-testid={`legrand-row-check-${i}`} type="checkbox" checked={!!sel[i]} disabled={!r.matched} onChange={() => toggleRow(i)} /></td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.legrand_ref}</td>
                    <td style={td}>{r.export_invoice}</td>
                    <td style={td}>{r.gst_invoice_number || "—"}</td>
                    <td style={{ ...td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{r.party || "—"}</td>
                    <td style={td}>{r.sale_no || "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{num(r.amount_usd)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.rate ? num(r.rate) : "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#B45309" }}>{r.bank_charge ? num(r.bank_charge) : "—"}</td>
                    <td style={{ ...td, fontWeight: 700, color: r.bank_ref ? "#166534" : "#94A3B8" }}>{r.bank_ref || "—"}</td>
                    <td style={td}>{fmtD(r.value_date)}</td>
                    <td style={td}>
                      {pr
                        ? (pr.ok
                            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: pr.already_recorded ? "#B45309" : "#166534", fontWeight: 700, fontSize: 12 }}><CheckCircle size={14} weight="fill" /> {pr.dry_run ? "Would post" : pr.already_recorded ? "Already recorded" : `Posted ${pr.voc_no || ""}`}</span>
                            : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#991B1B", fontWeight: 700, fontSize: 12 }}><WarningCircle size={14} weight="fill" /> {String(pr.error || "failed").slice(0, 36)}</span>)
                        : (r.matched
                            ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#166534", fontWeight: 700, fontSize: 12 }}><CheckCircle size={14} weight="fill" /> Matched</span>
                            : <span title={(r.flags || []).join("; ")} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#B45309", fontWeight: 700, fontSize: 12 }}><WarningCircle size={14} weight="fill" /> {(r.flags || [])[0] || "No bank advice"}</span>)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(data.not_matched || []).length > 0 && (
            <div data-testid="legrand-not-matched" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#991B1B", marginBottom: 8 }}>{data.not_matched.length} Legrand line(s) not found in the ERP</div>
              {data.not_matched.map((n, i) => (
                <div key={i} data-testid={`legrand-nm-${i}`} style={{ fontSize: 12.5, color: "#991B1B", padding: "2px 0" }}>
                  {n.legrand_ref} (export {n.export_invoice}) — {n.reason}
                </div>
              ))}
            </div>
          )}

          {(data.unused_advices || []).length > 0 && (
            <div data-testid="legrand-unused" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#92400E", marginBottom: 8 }}>{data.unused_advices.length} HDFC advice(s) did not match any Legrand line</div>
              {data.unused_advices.map((u, i) => (
                <div key={i} data-testid={`legrand-unused-${i}`} style={{ fontSize: 12.5, color: "#92400E", padding: "2px 0" }}>
                  {u.filename} — GST {u.gst_invoice_number || "—"}, bank ref {u.bank_ref || "—"}, ₹{num(u.inr_amount)}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!data && !busy && (
        <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", background: "#fff", border: "1px dashed #E2E8F0", borderRadius: 12 }}>
          <Receipt size={40} weight="thin" style={{ marginBottom: 8 }} />
          <div>Choose the Legrand advice + HDFC advices, then press <b>Parse &amp; match</b> to verify before posting.</div>
        </div>
      )}
    </div>
  );
}
